import type { WizardChatEnginePersistenceProps } from '@lemonade-hq/chat-wizard-client-base';
import type { UnionToIntersection } from '@lemonade-hq/ts-helpers';
import type { Brand, Scheme } from '@lemonade-hq/uniclient-theme';
import get from 'lodash/get';
import set from 'lodash/set';
import type { UniclientStepPresentation } from '../step';
import type {
  ClientUrlParams,
  DynamicClientUrlParamsMap,
  DynamicUrlParamsMap,
  KeysToObj,
  Preset,
  SubdomainParams,
} from './types';
import { AppStage, EdgeService, getEdgeServiceUrl } from './utils';

export class PresetSubdomainError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PresetSubdomainError';
  }
}

export const RESTART_QUERY_PARAM = 'f';
const DEFAULT_SUBDOMAIN = 'chat';

const DEFAULT_DYNAMIC_URL_PARAMS_MAP: DynamicUrlParamsMap = {
  [RESTART_QUERY_PARAM]: 'forceRestart',
  id: 'sessionId',
  'utm_.*': undefined,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _ec: 'emailCode',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _at: 'accessTokenOverride',
  '(.*)': 'initData.$1',
};

const DEFAULT_CLIENT_URL_PARAMS_MAP: DynamicClientUrlParamsMap = {
  s: 'initialStepName',
};

export const UNICLIENT_PRESETS = {
  'diy/change-payment-plan': {
    dynamicUrlParamsMap: {
      p: 'initData.policyPublicId',
    },
    staticParams: {
      scriptName: 'change-payment-plan-script',
      edgeName: EdgeService.UsersEdge,
    },
  },
  'diy/cancel-renewal': {
    dynamicUrlParamsMap: {
      p: 'initData.policyPublicId',
    },
    staticParams: {
      scriptName: 'uniclient-cancel-renewal-script',
      edgeName: EdgeService.UsersEdge,
    },
  },
  'diy/add-vet': {
    dynamicUrlParamsMap: {
      p: 'initData.policyPublicId',
    },
    staticParams: {
      scriptName: 'diy-add-vet-script',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'diy/update-phone': {
    staticParams: {
      scriptName: 'update-phone',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'diy/update-email': {
    staticParams: {
      scriptName: 'update-email',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'diy/change-cancellation-date': {
    dynamicUrlParamsMap: {
      p: 'initData.policyPublicId',
    },
    staticParams: {
      scriptName: 'diy-change-cancellation-date-script',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'car/onboarding': {
    dynamicUrlParamsMap: {
      sid: 'initData.oldChatSessionId',
      flow: 'initData.flow',
      aid: 'initData.addressId',
      yfsid: 'initData.youFlowSessionId',
      eqid: 'initData.estimatedQuotePublicId',
    },
    staticParams: {
      scriptName: 'car-onboarding',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
      allowedSubdomains: {
        'partner-chat': {
          theme: {
            scheme: 'dark',
            brand: 'bluish',
          },
          enforceInitData: {
            flow: 'partner-chat',
          },
        },
      },
    },
  },
  'car/insideSales': {
    dynamicUrlParamsMap: {
      sid: 'initData.oldChatSessionId',
    },
    staticParams: {
      scriptName: 'car-insideSales',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'car/inside-sales': {
    dynamicUrlParamsMap: {
      sid: 'initData.oldChatSessionId',
    },
    staticParams: {
      scriptName: 'car-inside-sales',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'car/quote': {
    dynamicUrlParamsMap: {
      sid: 'initData.oldChatSessionId',
      et: 'initData.endorsementType',
      pid: 'initData.policyPublicId',
      vid: 'initData.vehiclePublicId',
      // cspell:disable-next-line
      rvid: 'initData.replacedVehiclePublicId',
    },
    staticParams: {
      scriptName: 'car-quote',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 30_000,
    },
  },
  'car/partners-playground': {
    staticParams: {
      scriptName: 'car-partners-playground',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
      theme: {
        scheme: 'dark',
        brand: 'bluish',
      },
      enforceSubdomain: 'partner-chat',
      allowedSubdomains: {
        'partner-chat': {
          theme: {
            scheme: 'dark',
            brand: 'bluish',
          },
          enforceInitData: {
            flow: 'partner-chat',
          },
        },
      },
    },
  },
  'car/inside-sales-quote': {
    staticParams: {
      scriptName: 'car-inside-sales-quote',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'car/checkout': {
    staticParams: {
      scriptName: 'car-checkout',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'us/hs/homeowners/quote': {
    dynamicUrlParamsMap: { aid: 'initData.applicationId', mpid: 'initData.mppCartSessionId' },
    staticParams: {
      scriptName: 'us-hs-homeowners-quote',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 30_000,
    },
  },
  'us/hs/homeowners/post-purchase': {
    dynamicUrlParamsMap: { policyPublicId: 'initData.policyPublicId', mpid: 'initData.mppCartSessionId' },
    staticParams: {
      scriptName: 'us-hs-homeowners-post-purchase',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'affiliates/onboarding': {
    staticParams: {
      scriptName: 'affiliates-onboarding',
      edgeName: EdgeService.UsersEdge,
    },
  },
  'ams/meetup/survey': {
    staticParams: {
      scriptName: 'ams-meetup-survey',
      edgeName: EdgeService.UsersEdge,
    },
  },
  'diy/change-address/pet': {
    staticParams: {
      scriptName: 'change-address-script',
      edgeName: EdgeService.UsersEdge,
    },
    dynamicUrlParamsMap: {
      redirectedFromCancellation: 'initData.redirectedFromCancellation',
    },
  },
  'renters/us/quote': {
    dynamicUrlParamsMap: { isMpp: 'initData.isMpp' },
    staticParams: {
      scriptName: 'renters-us-quote',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'renters/us/quote-view': {
    dynamicUrlParamsMap: {
      quotePublicId: 'initData.quotePublicId',
      isMpp: 'initData.isMpp',
      lowerQuoteCoverage: 'initData.lowerQuoteCoverage',
      entryPoint: 'initData.entryPoint',
    },
    staticParams: {
      scriptName: 'renters-us-quote-view',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'renters/us/quote-checkout': {
    dynamicUrlParamsMap: { quotePublicId: 'initData.quotePublicId' },
    staticParams: {
      scriptName: 'renters-us-quote-checkout',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 30_000,
    },
  },
  'pet/us/quote': {
    dynamicUrlParamsMap: {
      isMpp: 'initData.isMpp',
      affiliateDiscountToken: 'initData.affiliateDiscountToken',
      partnerCode: 'initData.partnerCode',
    },
    staticParams: {
      scriptName: 'pet-us-quote',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'pet/us/quote/view': {
    dynamicUrlParamsMap: { quotePublicId: 'initData.quotePublicId', lqp: 'initData.lqp', expired: 'initData.expired' },
    staticParams: {
      scriptName: 'pet-us-quote-view',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'pet/us/quote/partners': {
    dynamicUrlParamsMap: { priceOfferId: 'initData.priceOfferId', token: 'initData.token' },
    staticParams: {
      scriptName: 'pet-us-quote-partners',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'pet/us/quote/partners/v2': {
    dynamicUrlParamsMap: { priceOfferId: 'initData.priceOfferId', token: 'initData.token' },
    staticParams: {
      scriptName: 'pet-us-quote-partners-v2',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  'pet/us/post-purchase': {
    dynamicUrlParamsMap: {
      policyPublicId: 'initData.policyPublicId',
      hideProgress: 'initData.hideProgress',
    },
    staticParams: {
      scriptName: 'pet-us-post-purchase',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'pet/us/post-purchase-followup': {
    dynamicUrlParamsMap: { policyPublicId: 'initData.policyPublicId', todoItemId: 'initData.todoItemId' },
    staticParams: {
      scriptName: 'pet-us-post-purchase-followup',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'home/claims/us/upload_file_agent': {
    dynamicUrlParamsMap: {
      cId: 'initData.claimPublicId',
      a: 'initData.actionName',
    },
    staticParams: {
      scriptName: 'home-claims-us-upload_file_agent',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 300_000,
    },
  },
  'home/claims/us/property_manager_details': {
    dynamicUrlParamsMap: {
      cId: 'initData.claimPublicId',
      a: 'initData.actionName',
    },
    staticParams: {
      scriptName: 'home-claims-us-property_manager_details',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'account/deletion': {
    staticParams: {
      scriptName: 'account-deletion',
      resourceType: 'page',
      edgeName: EdgeService.ChatPlatform,
    },
  },
  'homeowners/us/quote/view': {
    staticParams: {
      scriptName: 'homeowners-us-quote-view',
      resourceType: 'page',
      edgeName: EdgeService.ChatPlatform,
    },
    dynamicUrlParamsMap: { quotePublicId: 'initData.quotePublicId' },
  },
  checkout: {
    staticParams: {
      scriptName: 'checkout',
      edgeName: EdgeService.ChatPlatform,
      serverTimeoutOverride: 60_000,
    },
  },
  support: {
    staticParams: {
      scriptName: 'support',
      resourceType: 'conversationRouter',
      edgeName: EdgeService.ConversationRouter,
    },
  },
  default: {
    staticParams: {
      scriptName: (urlPath: string) => {
        return urlPath.replaceAll('/', '-');
      },
      edgeName: EdgeService.ChatPlatform,
    },
  },
} as const satisfies Record<string, Preset>;

type UniclientPresets = typeof UNICLIENT_PRESETS;
type PresetName = keyof UniclientPresets;

type PossibleParams<P extends PresetName> = UniclientPresets[P] extends Preset
  ? UniclientPresets[P]['dynamicUrlParamsMap']
  : never;

type ParamsMap<P extends PresetName> = PossibleParams<P>[keyof PossibleParams<P>] extends string
  ? UnionToIntersection<KeysToObj<PossibleParams<P>[keyof PossibleParams<P>]>>
  : never;

const APP_PATH_TRIGGER = 'app';
export const PROXY_PATH_TRIGGER = 'proxy';

export function isProxyRequest(path: string): boolean {
  return path.startsWith(`/${PROXY_PATH_TRIGGER}/`);
}

export function getUniclientPresetUrl<IPresetName extends PresetName>(
  presetName: IPresetName,
  env: `${AppStage}`,
  envId: string,
  params?: ParamsMap<IPresetName>,
  isPublic?: boolean,
  shouldOpenApp?: boolean,
): string {
  const { dynamicUrlParamsMap = {} } = UNICLIENT_PRESETS[presetName] as Preset;

  const uniclientUrl = getEdgeServiceUrl(EdgeService.Uniclient, env, envId, isPublic);

  const preset = shouldOpenApp ? `${APP_PATH_TRIGGER}/${presetName}` : presetName;

  const url = new URL(preset, uniclientUrl);

  Object.entries({ ...DEFAULT_DYNAMIC_URL_PARAMS_MAP, ...dynamicUrlParamsMap }).forEach(([key, value]) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const paramValue = get(params, value!);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- param value can be undefined
    if (paramValue == null) return;

    url.searchParams.set(key, paramValue as string);
  });

  return url.toString();
}

export type URLProps = Pick<URL, 'hostname' | 'pathname' | 'protocol' | 'search'>;

export const parseUniclientPresetUrl = (
  url: URLProps,
  env: `${AppStage}`,
  envId: string,
): WizardChatEnginePersistenceProps<UniclientStepPresentation> & {
  readonly resourceType?: 'chat' | 'conversationRouter' | 'page';
  readonly theme?: { readonly scheme: Scheme; readonly brand: Brand };
} => {
  const presetName = url.pathname
    .replace(`/${APP_PATH_TRIGGER}/`, '/')
    .replace(`/${PROXY_PATH_TRIGGER}/`, '/')
    .replaceAll(/^\/|\/$/g, '') as PresetName | '';
  const params = new URLSearchParams(url.search);

  if (presetName === '') {
    throw new Error('Preset name is missing');
  }

  let preset = UNICLIENT_PRESETS[presetName] as Preset | undefined;

  const isPlaygroundPreset = presetName.startsWith('playground');

  if (preset == null) {
    if (env !== AppStage.Production && isPlaygroundPreset) {
      const resolvedScript =
        (presetName as string) === 'playground' ? 'menu' : presetName.replace('playground/', '').replaceAll('/', '--');
      preset = {
        staticParams: {
          scriptName: resolvedScript,
          ...(resolvedScript.startsWith(`page-mechanism--`) ? { resourceType: 'page' } : {}),
          ...(resolvedScript === 'frame--theme'
            ? {
                theme: { scheme: 'dark', brand: 'bluish' },
              }
            : {}),
        },
      };
    } else {
      preset = UNICLIENT_PRESETS.default;
    }
  }

  let subdomainParams: SubdomainParams | undefined;
  if (env === AppStage.Production) {
    const currentSubdomain = window.location.hostname.split('.')[0];

    if (preset.staticParams.enforceSubdomain != null && currentSubdomain !== preset.staticParams.enforceSubdomain) {
      window.location.href = `https://${preset.staticParams.enforceSubdomain}.lemonade.com${url.pathname}${url.search}`;
      throw new PresetSubdomainError('Redirecting to enforced subdomain');
    }

    if (currentSubdomain !== DEFAULT_SUBDOMAIN) {
      if (Object.keys(preset.staticParams.allowedSubdomains ?? {}).includes(currentSubdomain)) {
        subdomainParams = preset.staticParams.allowedSubdomains?.[currentSubdomain];
      } else {
        window.location.href = `https://${DEFAULT_SUBDOMAIN}.lemonade.com${url.pathname}${url.search}`;
        throw new PresetSubdomainError('Subdomain not allowed');
      }
    }
  }

  const scriptName = getScriptName(preset, presetName);
  const restProps = restOfScriptProps(params, preset, subdomainParams);

  const serverBaseUrl =
    preset.staticParams.edgeName != null && !url.hostname.includes('localhost')
      ? getEdgeServiceUrl(preset.staticParams.edgeName, env, envId, url.hostname.includes('.public.'))
      : env === AppStage.Staging || (env === AppStage.Development && url.hostname.includes('.lmnddev.'))
        ? getEdgeServiceUrl('uniclient-playground' as EdgeService, env, envId, url.hostname.includes('.public.'))
        : `${url.protocol}//${url.hostname}:10700${isProxyRequest(url.pathname) ? `/proxy${isPlaygroundPreset ? '/playground' : ''}` : ''}`;

  return {
    scriptName,
    serverBaseUrl,
    serverTimeoutOverride: preset.staticParams.serverTimeoutOverride,
    resourceType: preset.staticParams.resourceType,
    ...restProps,
  };
};

export const parseClientUrlParams = (url: URLProps): ClientUrlParams => {
  const params = new URLSearchParams(url.search);
  return getParams(DEFAULT_CLIENT_URL_PARAMS_MAP, params);
};

function getScriptName(preset: Preset, presetName: string): string {
  const { scriptName } = preset.staticParams;
  if (typeof scriptName === 'string') return scriptName;

  return scriptName(presetName);
}

function restOfScriptProps(
  params: URLSearchParams,
  preset: Preset,
  subdomainParams?: SubdomainParams | undefined,
): Record<string, unknown> {
  const {
    dynamicUrlParamsMap = {},
    staticParams: { theme },
  } = preset;
  const { enforceInitData, theme: subdomainTheme } = subdomainParams ?? {};
  const dynamicUrlParams = { ...DEFAULT_DYNAMIC_URL_PARAMS_MAP, ...dynamicUrlParamsMap };
  return { ...getParams(dynamicUrlParams, params, enforceInitData), theme: subdomainTheme ?? theme };
}

function getParams(
  dynamicUrlParams: DynamicClientUrlParamsMap | DynamicUrlParamsMap,
  params: URLSearchParams,
  enforceInitData?: Record<string, string>,
): Record<string, unknown> {
  let scriptParams: Record<string, unknown> & { initData?: Record<string, unknown> } = {};

  for (const [paramKey, paramValue] of params.entries()) {
    const scriptParamKey = dynamicUrlParams[paramKey];
    const isForceRestart = paramKey === RESTART_QUERY_PARAM;
    if (scriptParamKey != null) {
      scriptParams = set(scriptParams, scriptParamKey, isForceRestart ? true : parseValue(paramValue));
    } else {
      const scriptParamKeyByRegex = getScriptParamKeyByRegex(dynamicUrlParams, paramKey);
      if (scriptParamKeyByRegex != null) {
        scriptParams = set(scriptParams, scriptParamKeyByRegex, parseValue(paramValue));
      }
    }
  }

  if (enforceInitData != null) {
    scriptParams.initData = { ...scriptParams.initData, ...enforceInitData };
  }

  return scriptParams;
}

function parseValue(value: string): boolean | string {
  if (value === 'true' || value === '') return true;
  if (value === 'false') return false;

  return value;
}

function getScriptParamKeyByRegex(
  dynamicUrlParams: DynamicClientUrlParamsMap | DynamicUrlParamsMap,
  paramKey: string,
): string | undefined {
  for (const [regexKey, regexValue] of Object.entries(dynamicUrlParams)) {
    const pattern = new RegExp(`^${regexKey}$`);
    if (pattern.test(paramKey)) {
      if (regexValue == null) return undefined;

      const scriptKey = paramKey.replace(pattern, regexValue);

      if (
        Object.values(dynamicUrlParams).includes(
          scriptKey as (DynamicClientUrlParamsMap | DynamicUrlParamsMap)[keyof (
            | DynamicClientUrlParamsMap
            | DynamicUrlParamsMap
          )],
        )
      ) {
        return undefined;
      }

      return scriptKey;
    }
  }

  /* istanbul ignore next -- @preserve - we support '.*' regex so we'll not get to here */
  return undefined;
}
